// src/components/AdminApprovalPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { db, Timestamp, auth } from '../firebase'; // Thêm auth để lấy currentUser
import {
  collectionGroup,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  orderBy,
  DocumentData,
  getDoc, // Để kiểm tra admin
} from 'firebase/firestore';
import { SavedLink } from './LinkSaver'; // Import interface SavedLink đã có
import styles from './AdminApprovalPage.module.css'; // Tạo file CSS riêng cho trang admin

interface PendingLink extends SavedLink {
  ownerUserId: string; // Thêm ownerUserId để biết link này của ai
}

const AdminApprovalPage: React.FC = () => {
  const [pendingLinks, setPendingLinks] = useState<PendingLink[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoadingAdminCheck, setIsLoadingAdminCheck] = useState<boolean>(true);
  const [rejectionNotes, setRejectionNotes] = useState<{ [linkId: string]: string }>({});


  // 1. Kiểm tra quyền Admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const adminDocRef = doc(db, 'admins', currentUser.uid);
        try {
          const adminDocSnap = await getDoc(adminDocRef);
          if (adminDocSnap.exists()) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
            setError("Bạn không có quyền truy cập trang này.");
          }
        } catch (err) {
          console.error("Lỗi kiểm tra quyền admin:", err);
          setIsAdmin(false);
          setError("Lỗi khi kiểm tra quyền admin.");
        }
      } else {
        setIsAdmin(false); // Chưa đăng nhập không phải admin
        setError("Vui lòng đăng nhập với tài khoản admin.");
      }
      setIsLoadingAdminCheck(false);
    };
    checkAdminStatus();
  }, []);

  // 2. Fetch các link đang chờ duyệt (nếu là admin)
  const fetchPendingLinks = useCallback(async () => {
    if (!isAdmin) return; // Chỉ fetch nếu là admin

    setIsLoading(true);
    setError(null);
    try {
      // Sử dụng collectionGroup query để lấy 'savedLinks' từ tất cả users
      const linksQuery = query(
        collectionGroup(db, 'savedLinks'),
        where('publicStatus', '==', 'pending'),
        orderBy('submittedForPublicAt', 'asc')
      );
      const querySnapshot = await getDocs(linksQuery);
      const fetchedLinks: PendingLink[] = [];
      querySnapshot.forEach((docSnap) => {
        // Lấy ownerUserId từ path của document: users/{ownerUserId}/savedLinks/{linkId}
        const ownerUserId = docSnap.ref.parent.parent?.id;
        if (ownerUserId) {
          fetchedLinks.push({
            ...(docSnap.data() as SavedLink),
            id: docSnap.id,
            ownerUserId: ownerUserId,
          });
        }
      });
      setPendingLinks(fetchedLinks);
    } catch (err) {
      console.error("Lỗi tải danh sách link chờ duyệt:", err);
      setError("Không thể tải danh sách link chờ duyệt. Vui lòng thử lại.");
    }
    setIsLoading(false);
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && !isLoadingAdminCheck) { // Chỉ fetch khi đã xác định là admin
      fetchPendingLinks();
    }
  }, [isAdmin, isLoadingAdminCheck, fetchPendingLinks]);


  // 3. Xử lý Approve/Reject
  const handleApprovalAction = async (
    linkId: string,
    ownerUserId: string,
    action: 'approved' | 'rejected',
    notes?: string
  ) => {
    if (!isAdmin) {
      alert("Bạn không có quyền thực hiện hành động này.");
      return;
    }
    const linkRef = doc(db, 'users', ownerUserId, 'savedLinks', linkId);
    try {
      const updatePayload: Partial<SavedLink> = {
        publicStatus: action,
        processedByAdminAt: Timestamp.now(),
      };
      if (action === 'rejected') {
        updatePayload.adminNotes = notes || "";
      } else {
        updatePayload.adminNotes = ""; // Xóa note cũ nếu approve
      }
      await updateDoc(linkRef, updatePayload);
      // Tải lại danh sách sau khi duyệt
      fetchPendingLinks();
      setRejectionNotes(prev => ({...prev, [linkId]: ""})); // Xóa note sau khi gửi
    } catch (err) {
      console.error(`Lỗi khi ${action === 'approved' ? 'chấp nhận' : 'từ chối'} link:`, err);
      alert(`Đã có lỗi xảy ra. Vui lòng thử lại.`);
    }
  };
  
  const handleNoteChange = (linkId: string, note: string) => {
    setRejectionNotes(prev => ({...prev, [linkId]: note}));
  };


  // --- Render UI ---
  if (isLoadingAdminCheck) {
    return <div className={styles.adminPage}><p>Đang kiểm tra quyền truy cập...</p></div>;
  }

  if (!isAdmin) {
    return <div className={styles.adminPage}><p className={styles.error}>{error || "Bạn không có quyền truy cập."}</p></div>;
  }
  
  if (isLoading) {
    return <div className={styles.adminPage}><p>Đang tải danh sách link chờ duyệt...</p></div>;
  }

  if (error) {
    return <div className={styles.adminPage}><p className={styles.error}>{error}</p></div>;
  }

  return (
    <div className={styles.adminPage}>
      <h1>Duyệt Link Công Khai</h1>
      {pendingLinks.length === 0 ? (
        <p>Không có link nào đang chờ duyệt.</p>
      ) : (
        <table className={styles.linksTable}>
          <thead>
            <tr>
              <th>Tiêu đề</th>
              <th>URL</th>
              <th>Người gửi (User ID)</th>
              <th>Ngày gửi</th>
              <th>Ghi chú từ chối</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {pendingLinks.map((link) => (
              <tr key={link.id}>
                <td>{link.title}</td>
                <td><a href={link.url} target="_blank" rel="noopener noreferrer">{link.url.substring(0,50)}...</a></td>
                <td>{link.ownerUserId}</td>
                <td>{link.submittedForPublicAt ? new Date(link.submittedForPublicAt.toDate()).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <textarea 
                    className={styles.notesInput}
                    placeholder="Lý do từ chối (nếu có)"
                    value={rejectionNotes[link.id] || ""}
                    onChange={(e) => handleNoteChange(link.id, e.target.value)}
                  />
                </td>
                <td className={styles.actionsCell}>
                  <button 
                    onClick={() => handleApprovalAction(link.id, link.ownerUserId, 'approved')}
                    className={`${styles.actionButton} ${styles.approveButton}`}
                  >
                    Chấp nhận
                  </button>
                  <button 
                    onClick={() => handleApprovalAction(link.id, link.ownerUserId, 'rejected', rejectionNotes[link.id])}
                    className={`${styles.actionButton} ${styles.rejectButton}`}
                  >
                    Từ chối
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminApprovalPage;